(() => {
  if (customElements.get('powr-mix-match')) return;

  class PowrMixMatch extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.productId = String(this.dataset.productId || '');
      this.productHandle = this.dataset.productHandle || '';
      this.bundleTitle = this.dataset.bundleTitle || 'Mix & Match';
      this.bundleImage = this.dataset.bundleImage || '';
      this.editUrl = this.dataset.editUrl || (this.productHandle ? `/products/${this.productHandle}` : '');
      this.unitNoun = this.dataset.unitNoun || 'dozen';
      this.washesPerUnit = Number(this.dataset.washesPerUnit || 0);
      this.singleUnitCents = Number(this.dataset.singleUnitCents || 0);
      this.defaultTier = Number(this.dataset.defaultTier || 6);
      this.currency = this.dataset.currency || 'EUR';
      this.locale = this.dataset.locale || 'nl-NL';
      this.washesLabel = this.dataset.washesLabel || 'wasbeurten';
      this.perWashLabel = this.dataset.perWashLabel || '/wasbeurt';
      this.summarySeparator = this.dataset.summarySeparator || '·';
      this.step2Template = this.dataset.step2HeadingTemplate || 'Verdeel je {count} {noun}';
      this.incompleteMessage = this.dataset.incompleteMessage || 'Verdeel eerst je bundel over de kleuren ({current}/{total})';
      this.atcLabel = this.dataset.atcLabel || 'IN WINKELWAGEN';

      this.variants = this.parseJson('[data-powr-mm-variants]', []);
      this.colours = this.parseJson('[data-powr-mm-colours]', []);
      this.qtyByVariant = new Map(this.colours.map((colour) => [String(colour.variantId), 0]));

      this.tiersEl = this.querySelector('[data-powr-mm-tiers]');
      this.step2TitleEl = this.querySelector('[data-powr-mm-step2-title]');
      this.progressEl = this.querySelector('[data-powr-mm-progress]');
      this.progressCheckEl = this.querySelector('[data-powr-mm-progress-check]');
      this.progressTextEl = this.querySelector('[data-powr-mm-progress-text]');
      this.summaryMetaEl = this.querySelector('[data-powr-mm-summary-meta]');
      this.summaryCompareEl = this.querySelector('[data-powr-mm-summary-compare]');
      this.summaryTotalEl = this.querySelector('[data-powr-mm-summary-total]');
      this.hintEl = this.querySelector('[data-powr-mm-hint]');
      this.atcButton = this.querySelector('[data-powr-mm-atc]');
      this.atcLabelEl = this.querySelector('[data-powr-mm-atc-label]');
      this.statusEl = this.querySelector('[data-powr-mm-status]');
      this.colourRows = [...this.querySelectorAll('[data-powr-mm-colour]')];

      const initialTierButton = this.querySelector(`[data-powr-mm-tier][data-tier="${this.defaultTier}"]`)
        || this.querySelector('[data-powr-mm-tier]');
      this.selectedTier = Number(initialTierButton?.dataset.tier || this.defaultTier);
      this.selectedVariantId = String(initialTierButton?.dataset.variantId || '');

      this.tiersEl?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-powr-mm-tier]');
        if (!button || !this.tiersEl.contains(button)) return;
        this.selectTier(Number(button.dataset.tier), String(button.dataset.variantId));
      });

      this.colourRows.forEach((row) => {
        const variantId = String(row.dataset.variantId || '');
        row.querySelector('[data-powr-mm-minus]')?.addEventListener('click', () => {
          this.adjustQty(variantId, -1);
        });
        row.querySelector('[data-powr-mm-plus]')?.addEventListener('click', () => {
          this.adjustQty(variantId, 1);
        });
      });

      this.atcButton?.addEventListener('click', () => {
        this.addToCart();
      });

      this.render();
      this.hydrateFromCart();
    }

    parseJson(selector, fallback) {
      const node = this.querySelector(selector);
      if (!node) return fallback;

      try {
        return JSON.parse(node.textContent || 'null') || fallback;
      } catch (error) {
        console.warn('[PowrMixMatch] Failed to parse JSON', selector, error);
        return fallback;
      }
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
        }).format(amount);
      } catch (error) {
        return `€${amount.toFixed(2).replace('.', ',')}`;
      }
    }

    selectedVariant() {
      return this.variants.find((variant) => Number(variant.tier) === Number(this.selectedTier))
        || this.variants.find((variant) => String(variant.id) === String(this.selectedVariantId))
        || this.variants[0]
        || null;
    }

    totalQty() {
      let total = 0;
      for (const qty of this.qtyByVariant.values()) total += Number(qty) || 0;
      return total;
    }

    isComplete() {
      return this.totalQty() === Number(this.selectedTier);
    }

    selectTier(tier, variantId) {
      if (Number(tier) === Number(this.selectedTier)) return;

      this.selectedTier = Number(tier);
      this.selectedVariantId = String(variantId || '');

      for (const key of this.qtyByVariant.keys()) {
        this.qtyByVariant.set(key, 0);
      }

      this.render();
    }

    adjustQty(variantId, delta) {
      const key = String(variantId);
      const current = Number(this.qtyByVariant.get(key) || 0);
      const total = this.totalQty();
      const tier = Number(this.selectedTier);
      let next = current + delta;

      if (next < 0) next = 0;
      if (delta > 0 && total >= tier) return;
      if (delta > 0 && total - current + next > tier) {
        next = Math.max(0, tier - (total - current));
      }

      this.qtyByVariant.set(key, next);
      this.render();
    }

    render() {
      const variant = this.selectedVariant();
      const tier = Number(this.selectedTier);
      const total = this.totalQty();
      const complete = this.isComplete();
      const bundlePrice = Number(variant?.price || 0);
      const compareAt = this.singleUnitCents > 0
        ? this.singleUnitCents * tier
        : Number(variant?.compareAtPrice || 0);
      const washes = tier * this.washesPerUnit;
      const perWash = washes > 0 ? Math.round(bundlePrice / washes) : 0;

      this.querySelectorAll('[data-powr-mm-tier]').forEach((button) => {
        const selected = Number(button.dataset.tier) === tier;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', selected ? 'true' : 'false');

        const unitEl = button.querySelector('.powr-mm__tier-unit');
        if (unitEl) {
          const buttonTier = Number(button.dataset.tier || 0);
          const buttonPrice = Number(button.dataset.price || 0);
          const unitPrice = buttonTier > 0 ? Math.round(buttonPrice / buttonTier) : 0;
          unitEl.textContent = `${this.formatMoney(unitPrice)}${this.dataset.perUnitLabel || '/stuk'}`;
        }

        const priceEl = button.querySelector('.powr-mm__tier-price');
        if (priceEl) {
          priceEl.textContent = this.formatMoney(Number(button.dataset.price || 0));
        }
      });

      if (this.step2TitleEl) {
        this.step2TitleEl.textContent = this.step2Template
          .replace('{count}', String(tier))
          .replace('{noun}', this.unitNoun);
      }

      if (this.progressEl && this.progressTextEl) {
        this.progressTextEl.textContent = `${total}/${tier}`;
        this.progressEl.classList.toggle('is-complete', complete);
        if (this.progressCheckEl) this.progressCheckEl.hidden = !complete;
      }

      this.colourRows.forEach((row) => {
        const variantId = String(row.dataset.variantId || '');
        const qty = Number(this.qtyByVariant.get(variantId) || 0);
        const qtyEl = row.querySelector('[data-powr-mm-qty]');
        const minus = row.querySelector('[data-powr-mm-minus]');
        const plus = row.querySelector('[data-powr-mm-plus]');

        if (qtyEl) qtyEl.textContent = String(qty);
        if (minus) minus.disabled = qty <= 0;
        if (plus) plus.disabled = total >= tier;
      });

      if (this.summaryMetaEl) {
        this.summaryMetaEl.textContent = `${tier} ${this.unitNoun} ${this.summarySeparator} ${washes} ${this.washesLabel}`;
      }

      if (this.summaryCompareEl) {
        if (compareAt > bundlePrice) {
          this.summaryCompareEl.hidden = false;
          this.summaryCompareEl.textContent = this.formatMoney(compareAt);
        } else {
          this.summaryCompareEl.hidden = true;
          this.summaryCompareEl.textContent = '';
        }
      }

      if (this.summaryTotalEl) {
        this.summaryTotalEl.textContent = this.formatMoney(bundlePrice);
      }

      if (this.hintEl) {
        if (complete) {
          this.hintEl.hidden = true;
          this.hintEl.textContent = '';
        } else {
          this.hintEl.hidden = false;
          this.hintEl.textContent = this.incompleteMessage
            .replace('{current}', String(total))
            .replace('{total}', String(tier));
        }
      }

      if (this.atcButton) {
        this.atcButton.disabled = !complete || !variant?.id;
      }

      this.updateHeaderPrice(bundlePrice, perWash, washes);
    }

    updateHeaderPrice(bundlePrice, perWashCents, washes) {
      const priceHtml = `<span class="price">${this.formatMoney(bundlePrice)}</span>`;

      // Desktop header uses #pdp-price; mobile header uses .mobileblock .secondblock (no id).
      for (const priceRoot of document.querySelectorAll('#pdp-price, .mobileblock .secondblock')) {
        // Header shows pack price only; compare-at lives in the Mix & Match summary bar.
        priceRoot.innerHTML = priceHtml;
      }

      const dosageNodes = document.querySelectorAll('.dosage-price');
      for (const dosage of dosageNodes) {
        if (perWashCents > 0) {
          dosage.textContent = `${this.formatMoney(perWashCents)}${this.perWashLabel}`;
        }
      }

      const subtitle = document.querySelector('.product-information-mix-match .pb-rp-text, .pb-rp-text');
      if (subtitle && washes > 0) {
        const labelEl = subtitle.querySelector('.pb-rp-text__label');
        const base = (labelEl?.textContent || subtitle.textContent || '')
          .split('|')[0]
          .trim() || 'Mix & match';
        subtitle.innerHTML =
          `<span class="pb-rp-text__label">${base}</span>` +
          `<span class="pb-rp-text__sep"> | </span>` +
          `<span class="pb-rp-text__washes">${washes} wasbeurten</span>`;
      }

      const mobileSubtitle = document.querySelector(
        '.product-information-mix-match .mobiletitlesubtitle, .mobiletitlesubtitle'
      );
      if (mobileSubtitle && washes > 0) {
        const mobileBase = (mobileSubtitle.textContent || '').split('|')[0].trim() || 'Mix & match';
        mobileSubtitle.textContent = `${mobileBase} | ${washes} wasbeurten`;
      }
    }

    buildCartItems(bundleId) {
      const items = [];
      const variant = this.selectedVariant();
      const tier = Number(this.selectedTier);
      const packPrice = Number(variant?.price || 0);
      const packCompare = this.singleUnitCents > 0
        ? this.singleUnitCents * tier
        : Number(variant?.compareAtPrice || 0);

      for (const colour of this.colours) {
        const variantId = String(colour.variantId || '');
        const qty = Number(this.qtyByVariant.get(variantId) || 0);
        if (!variantId || qty <= 0) continue;

        items.push({
          id: Number(variantId),
          variantId: Number(variantId),
          quantity: qty,
          title: colour.label || '',
          image: colour.image || '',
          properties: {
            _powr_mixmatch: 'true',
            _powr_mixmatch_id: bundleId,
            _powr_mixmatch_product_id: this.productId,
            _powr_mixmatch_title: this.bundleTitle,
            _powr_mixmatch_image: this.bundleImage,
            _powr_mixmatch_tier: String(tier),
            _powr_mixmatch_unit_noun: this.unitNoun,
            _powr_mixmatch_card_title: colour.label || '',
            _powr_mixmatch_edit_url: this.editUrl,
            _powr_mixmatch_price: String(packPrice),
            _powr_mixmatch_compare_at: String(packCompare),
          },
        });
      }

      return items;
    }

    async waitForCart(timeout = 8000) {
      const started = Date.now();

      while (!window.PBCart?.replaceMixMatchBundle) {
        if (Date.now() - started > timeout) {
          throw new Error('Mix & Match cart integration is unavailable');
        }
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      return window.PBCart;
    }

    async hydrateFromCart() {
      try {
        const cart = await this.waitForCart();
        const current = await cart.refresh();
        const lines = (current?.lines?.edges || []).map((edge) => edge.node);
        const matchLines = lines.filter((line) => {
          const attrs = Object.fromEntries((line.attributes || []).map((attr) => [attr.key, attr.value]));
          return attrs._powr_mixmatch === 'true' && attrs._powr_mixmatch_product_id === this.productId;
        });

        if (!matchLines.length) return;

        const firstAttrs = Object.fromEntries(
          (matchLines[0].attributes || []).map((attr) => [attr.key, attr.value])
        );
        const tier = Number(firstAttrs._powr_mixmatch_tier || 0);
        const tierVariant = this.variants.find((variant) => Number(variant.tier) === tier);

        if (tierVariant) {
          this.selectedTier = Number(tierVariant.tier);
          this.selectedVariantId = String(tierVariant.id);
        }

        for (const key of this.qtyByVariant.keys()) {
          this.qtyByVariant.set(key, 0);
        }

        // Heeft de cart transform de bundel samengevoegd, dan is er één regel op
        // het packproduct en zitten de kleuren in lineComponents. Zonder die
        // transform staan de kleurregels er gewoon los. Beide vormen leveren
        // dezelfde lijst op, zodat de picker in allebei de gevallen invult.
        const colourLines = matchLines.flatMap((line) => {
          const components = line.lineComponents || [];
          return components.length ? components : [line];
        });

        for (const line of colourLines) {
          const variantId = String(line.merchandise?.id || '').replace(/\D/g, '');
          if (!variantId || !this.qtyByVariant.has(variantId)) continue;
          const qty = Number(line.quantity || 0);
          this.qtyByVariant.set(variantId, qty);
        }

        this.render();
      } catch (error) {
        console.warn('[PowrMixMatch] Prefill from cart skipped', error);
      }
    }

    async addToCart() {
      if (!this.atcButton || this.atcButton.disabled) return;
      if (!this.isComplete()) return;

      const bundleId = `mixmatch_${this.productId}_${Date.now()}`;
      const cartItems = this.buildCartItems(bundleId);
      if (!cartItems.length) return;

      const defaultLabel = this.atcLabel;
      this.atcButton.disabled = true;
      this.atcButton.setAttribute('aria-busy', 'true');
      if (this.atcLabelEl) this.atcLabelEl.textContent = 'Toevoegen…';
      if (this.statusEl) this.statusEl.textContent = '';

      try {
        const cart = await this.waitForCart();
        await cart.replaceMixMatchBundle({
          productId: this.productId,
          bundleId,
          bundleTitle: this.bundleTitle,
          bundleImage: this.bundleImage,
          editUrl: this.editUrl,
          cartItems,
        });
        if (this.statusEl) this.statusEl.textContent = 'Je bundel is toegevoegd.';
      } catch (error) {
        console.error('[PowrMixMatch] Add to cart failed', error);
        if (this.statusEl) this.statusEl.textContent = 'Toevoegen is niet gelukt. Probeer het opnieuw.';
      } finally {
        this.atcButton.removeAttribute('aria-busy');
        if (this.atcLabelEl) this.atcLabelEl.textContent = defaultLabel;
        this.render();
      }
    }
  }

  customElements.define('powr-mix-match', PowrMixMatch);
})();
